import type {AnyField, JsonValue} from "./types.js";

export type AggregationKind = "sum" | "min" | "max" | "count" | "avg" | "distinct";

/** What the backend is asked to compute, over the whole filtered set rather than one page. */
export interface AggregationJson {
    readonly aggregation: AggregationKind;
    readonly field: string;
}

/** One computed summary as it comes back in the list metadata. */
export interface AggregationResult {
    readonly aggregation: string;
    readonly field: string;
    readonly result: JsonValue;
}

export class Aggregation {
    constructor(
        readonly kind: AggregationKind,
        readonly field: string,
        readonly label: string
    ) {
    }

    get json(): AggregationJson {
        return {aggregation: this.kind, field: this.field};
    }

    matches(result: AggregationResult): boolean {
        return result.field === this.field && result.aggregation === this.kind;
    }
}

const of = (kind: AggregationKind) => (source: AnyField, label?: string): Aggregation => {
    const name = source.name;
    if (name === undefined) {
        throw new Error(`Cannot aggregate an unnamed field with "${kind}"`);
    }
    return new Aggregation(kind, name, label ?? source.label);
};

export const aggregation = {
    sum: of("sum"),
    min: of("min"),
    max: of("max"),
    count: of("count"),
    avg: of("avg"),
    distinct: of("distinct")
} as const;

/** The years a list actually contains, which the year filter tags are built from. */
export const isDistinctYears = (result: AggregationResult): boolean =>
    result.aggregation === "distinct"
    && result.field.toLowerCase().endsWith("year")
    && Array.isArray(result.result)
    && result.result.length > 1;
